import React, { useState, useEffect } from "react";
import {
  Box,
  styled,
  Table,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  Typography,
} from "@mui/material";

import { HtmlTooltip } from "../../component/Methods";
import { formatTimeAgo } from "../../utils/helpers";

const Tablecell = styled(TableCell)(({ theme }) => ({
  fontSize: "12px",
  padding: "1px 2px 1px 5px",
}));

export const BENCHMARK_ROW_MIN_HEIGHT = 23;
const BENCHMARK_BODY_ROWS = 5;

const BenchMarkTable = ({ marketData, compact = false }) => {
  const [benchmarksWithMarketData, setBenchmarksWithMarketData] = useState([]);
  const theme = useTheme();

  useEffect(() => {
    const benchmarks = [
      { name: "NASDAQ", symbol2: "qqq" },
      { name: "S&P 500", symbol2: "spy" },
      { name: "RUSSELL 2000", symbol2: "iwm" },
      { name: "DOW JONES", symbol2: "dia" },
      { name: "RUSSELL 3000", symbol2: "iyk" },
    ];

    const updated = benchmarks.map((benchmark) => {
      const matchingData2 = marketData?.find(
        (data) => data?.symbol === benchmark.symbol2
      );
      const toReturn = {
        name: benchmark.name,
        symbol2: benchmark.symbol2,
        val2: 0,
        date2: "N/A",
      };

      if (matchingData2) {
        const { price, yestPrice, expiration } = matchingData2;
        const chPer = (((price - yestPrice) / yestPrice) * 100).toFixed(2);
        toReturn.val2 = chPer;
        toReturn.date2 = expiration;
      }
      return toReturn;
    });
    setBenchmarksWithMarketData(updated);
  }, [marketData]);

  const compactHeight =
    BENCHMARK_BODY_ROWS * BENCHMARK_ROW_MIN_HEIGHT + 28;

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
        borderRadius: "4px",
        width: "100%",
        height: compact ? compactHeight : "100%",
        display: "flex",
        flexDirection: "column",
        alignSelf: compact ? "flex-start" : "stretch",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: theme.palette.lightGrey.tableHeader,
          px: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: theme.palette.lightGrey.textColor }}
        >
          Benchmark Intraday Performance
        </Typography>
      </Box>
      <Table
        size="small"
        sx={{ tableLayout: "fixed", width: "100%" }}
      >
        <colgroup>
          <col style={{ width: "52%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "30%" }} />
        </colgroup>

        <TableBody>
          {benchmarksWithMarketData.map((row) => (
            <TableRow
              key={row.name}
              sx={{
                height: BENCHMARK_ROW_MIN_HEIGHT,
                "& .MuiTableCell-root": {
                  height: BENCHMARK_ROW_MIN_HEIGHT,
                  boxSizing: "border-box",
                },
                "&:hover": {
                  background: theme.palette.grey[200],
                  cursor: "pointer",
                },
              }}
            >
              <Tablecell
                align="left"
                sx={{
                  color: theme.palette.lightGrey.textColor,
                  fontSize: theme.typography.bodyText12.fontSize,
                  fontWeight: theme.typography.bodyText12.fontWeight,
                  borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row?.name}
              </Tablecell>

              <Tablecell
                align="left"
                sx={{
                  fontWeight: "bold",
                  borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.symbol2}
              </Tablecell>
              <Tablecell
                align="right"
                sx={{
                  color:
                    row.val2 >= 0
                      ? theme.palette.mode === "dark"
                        ? "#4ade80"
                        : "#16a34a"
                      : theme.palette.mode === "dark"
                      ? "#f87171"
                      : "#dc2626",
                }}
              >
                <HtmlTooltip
                  title={
                    <React.Fragment>
                      <Typography color="inherit" sx={{ fontSize: "12px" }}>
                        Market:<b> Primary </b>
                      </Typography>
                      <Typography color="inherit" sx={{ fontSize: "12px" }}>
                        Updated: <b>{formatTimeAgo(row?.date2).msg}</b>
                      </Typography>
                    </React.Fragment>
                  }
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: "100%",
                      textAlign: "right",
                    }}
                  >
                    {row.val2 >= 0 ? (
                      <>{row.val2}%&nbsp;</>
                    ) : (
                      `(${row.val2 * -1}%)`
                    )}
                  </Box>
                </HtmlTooltip>
              </Tablecell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default React.memo(BenchMarkTable);
